import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';

// Component imports
import { AttendeeListItem } from './AttendeeListItem';
import { FilterModal } from './FilterModal';
import { ResidentDetailsModal } from './ResidentDetailsModal';

// Types
import { EventAttendanceTabProps } from './types';
import { StatsCard } from '@/components/common/admin/mobile/StatsCard';
import { useEventAttendance } from './hooks/useEventAttendance';
import { DesktopResidentDetailsPanel } from './components/DesktopResidentDetailsPanel';
import { PaginationControls } from './components/PaginationControls';
import { FilterDropdown } from './components/FilterDropdown';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassFormInput } from '@/components/admin/redesign';
import { EventAttendanceTabSkeleton } from './EventAttendanceTabSkeleton';

export const EventAttendanceTab: React.FC<EventAttendanceTabProps> = ({ event }) => {
  const state = useEventAttendance(event);
  const { redesign, semantic } = useAdminColors();

  // state & handlers
  const {
    attendeeList,
    totalAttendees,
    totalPages,
    stats,
    selectedResident,
    selectedResidentFlags,
    currentPage,
    searchQuery,
    showFilters,
    showDropdown,
    triggerRef,
    anchor,
    isFetching,
    isLoading,
    error,
    pagingForward,
    isFilterRefetch,
    lastRefetchSource,
    isUpdatingRsvp,
    attendeeFilters,
    hasActiveFilters,
    showSplitDetails,
    residentModalOpen,
    setShowFilters,
    setShowDropdown,
    setAnchor,
    setSelectedResident,
    setResidentModalOpen,
    handlePreviousPage,
    handleNextPage,
    toggleFilter,
    onSearchChange,
    handleCheckIn,
    handleExport,
  } = state as any;

  // Initial loading state (no data yet)
  const isInitialLoad = isLoading && !state.attendanceData;
  if (isInitialLoad) {
    return <EventAttendanceTabSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={[styles.errorText, { color: semantic.status.error }]}>
          Failed to load attendance
        </Text>
        <Text style={[styles.errorSubtext, { color: redesign.text.muted }]}>
          Please try again later
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Stats Section */}
      <View style={styles.statsSection}>
        <View style={styles.statsRow}>
          <StatsCard
            title="Checked In"
            value={stats.checkedIn}
            color={semantic.status.success}
            icon={<SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color="white" />}
          />
          <StatsCard
            title="Total RSVPs"
            value={stats.rsvps}
            color={semantic.status.info}
            icon={<SVG_COMPONENTS.GROUP width={16} height={16} color="white" />}
          />
        </View>
      </View>

      {/* Search and Filter Section */}
      <View style={styles.controlsSection}>
        <View style={styles.searchContainer}>
          <View style={styles.searchWrapper}>
            <GlassFormInput
              placeholder="Search residents..."
              value={searchQuery}
              onChangeText={onSearchChange}
              accessibilityLabel="Search residents"
              accessibilityHint="Type to search for residents by name"
              accessibilityRole="search"
              containerStyle={styles.searchInputContainer}
              inputStyle={
                isFetching && isFilterRefetch && lastRefetchSource === 'search'
                  ? { paddingRight: 40 }
                  : undefined
              }
            />
            {isFetching && isFilterRefetch && lastRefetchSource === 'search' && (
              <View style={styles.searchSpinnerContainer} pointerEvents="none">
                <ActivityIndicator size="small" color={redesign.text.muted} />
              </View>
            )}
          </View>
        </View>
        {Platform.OS === 'web' ? (
          <View style={styles.filterDropdownContainer} ref={triggerRef}>
            <TouchableOpacity
              style={[
                styles.filterButtonIcon,
                {
                  backgroundColor: redesign.glass.buttonBg,
                  borderColor: redesign.card.border,
                },
                showDropdown && {
                  backgroundColor: redesign.accent.blueBgLight,
                  borderColor: redesign.accent.blueBg,
                },
              ]}
              onPress={() => {
                const node: any = triggerRef.current;
                if (node && node.measureInWindow) {
                  node.measureInWindow((x: number, y: number, w: number, h: number) => {
                    setAnchor({ x, y, w, h });
                    setShowDropdown(true);
                  });
                } else {
                  setShowDropdown(true);
                }
              }}
              accessibilityLabel={
                hasActiveFilters
                  ? 'Filter attendees (filters active)'
                  : 'Filter attendees'
              }
              accessibilityHint="Tap to toggle filter options"
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.FILTER
                width={16}
                height={16}
                color={redesign.text.primary}
              />
              {hasActiveFilters && (
                <View
                  style={[styles.filterDot, { backgroundColor: redesign.accent.blueBg }]}
                />
              )}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={[
              styles.filterButton,
              {
                backgroundColor: redesign.glass.buttonBg,
                borderColor: redesign.card.border,
              },
              hasActiveFilters && {
                backgroundColor: redesign.accent.blueBgLight,
                borderColor: redesign.accent.blueBg,
              },
            ]}
            onPress={() => setShowFilters(true)}
            accessibilityLabel={
              hasActiveFilters
                ? 'Filter attendees (filters active)'
                : 'Filter attendees'
            }
            accessibilityHint="Tap to open filter options for attendee list"
            accessibilityRole="button"
          >
            <Text style={[styles.filterButtonText, { color: redesign.text.primary }]}>
              Filter
            </Text>
            {hasActiveFilters && (
              <View
                style={[styles.filterDot, { backgroundColor: redesign.accent.blueBg }]}
              />
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* Web dropdown rendered via anchored portal (handles backdrop close internally) */}
      {Platform.OS === 'web' && showDropdown && (
        <FilterDropdown
          anchor={anchor}
          filters={attendeeFilters}
          onToggle={toggleFilter}
          onRequestClose={() => setShowDropdown(false)}
        />
      )}

      {showSplitDetails ? (
        <View style={styles.desktopRow}>
          <View
            style={[
              styles.desktopListColumn,
              { borderRightColor: redesign.card.border },
            ]}
          >
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={[styles.listTitle, { color: redesign.text.primary }]}>
                  Check-in Status ({totalAttendees})
                </Text>
                {Platform.OS === 'web' && (
                  <TouchableOpacity
                    style={[
                      styles.exportButton,
                      { backgroundColor: semantic.interactive.primary },
                    ]}
                    accessibilityLabel="Export attendee list"
                    accessibilityHint="Tap to export the current attendee list to CSV"
                    accessibilityRole="button"
                    onPress={handleExport}
                  >
                    <Text style={styles.exportButtonText}>Export</Text>
                  </TouchableOpacity>
                )}
              </View>
              {isFetching &&
                !pagingForward &&
                isFilterRefetch &&
                lastRefetchSource === 'filter' && (
                  <View style={styles.listLoadingOverlay} pointerEvents="none">
                    <View style={styles.listLoadingInner}>
                      <ActivityIndicator size="small" color={semantic.text.link} />
                      <Text
                        style={[
                          styles.listLoadingText,
                          { color: redesign.text.muted },
                        ]}
                      >
                        Updating list...
                      </Text>
                    </View>
                  </View>
                )}
              {!isFetching && attendeeList.length === 0 ? (
                <View style={styles.noAttendeesContainer}>
                  <Text
                    style={[styles.noAttendeesText, { color: redesign.text.primary }]}
                  >
                    No attendees found
                  </Text>
                  <Text
                    style={[styles.noAttendeesSubtext, { color: redesign.text.muted }]}
                  >
                    Try adjusting your filters or search query
                  </Text>
                </View>
              ) : (
                <>
                  <FlatList
                    data={attendeeList}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={({ item }) => (
                      <AttendeeListItem
                        resident={item}
                        eventId={event.id}
                        onPress={() => setSelectedResident(item)}
                      />
                    )}
                    style={styles.attendeeList}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={styles.listContent}
                  />
                  {totalPages > 1 && (
                    <PaginationControls
                      page={currentPage}
                      totalPages={totalPages}
                      isFetching={isFetching}
                      pagingForward={pagingForward}
                      onPrev={handlePreviousPage}
                      onNext={handleNextPage}
                    />
                  )}
                </>
              )}
            </View>
          </View>
          <View style={styles.desktopDetailsColumn}>
            {!selectedResident ? (
              <View style={styles.desktopEmptyState}>
                <Text
                  style={[
                    styles.desktopEmptyTitle,
                    { color: redesign.text.primary },
                  ]}
                >
                  Select resident
                </Text>
                <Text
                  style={[
                    styles.desktopEmptySubtitle,
                    { color: redesign.text.muted },
                  ]}
                >
                  Review the resident's information and check them in
                </Text>
              </View>
            ) : (
              <DesktopResidentDetailsPanel
                resident={selectedResident}
                flags={selectedResidentFlags}
                isUpdating={isUpdatingRsvp}
                onCheckIn={handleCheckIn}
              />
            )}
          </View>
        </View>
      ) : (
        <>
          {/* Attendee List (mobile) */}
          <View style={styles.listSection}>
            <View style={styles.listHeader}>
              <Text style={[styles.listTitle, { color: redesign.text.primary }]}>
                Check-in Status ({totalAttendees})
              </Text>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={[
                    styles.exportButton,
                    { backgroundColor: semantic.interactive.primary },
                  ]}
                  accessibilityLabel="Export attendee list"
                  accessibilityHint="Tap to export the current attendee list to CSV"
                  accessibilityRole="button"
                  onPress={handleExport}
                >
                  <Text style={styles.exportButtonText}>Export</Text>
                </TouchableOpacity>
              )}
            </View>
            {isFetching &&
              !pagingForward &&
              isFilterRefetch &&
              lastRefetchSource === 'filter' && (
                <View style={styles.listLoadingOverlay} pointerEvents="none">
                  <View style={styles.listLoadingInner}>
                    <ActivityIndicator size="small" color={semantic.text.link} />
                    <Text
                      style={[styles.listLoadingText, { color: redesign.text.muted }]}
                    >
                      Updating list...
                    </Text>
                  </View>
                </View>
              )}
            {!isFetching && attendeeList.length === 0 ? (
              <View style={styles.noAttendeesContainer}>
                <Text style={[styles.noAttendeesText, { color: redesign.text.primary }]}>
                  No attendees found
                </Text>
                <Text style={[styles.noAttendeesSubtext, { color: redesign.text.muted }]}>
                  Try adjusting your filters or search query
                </Text>
              </View>
            ) : (
              <>
                <FlatList
                  data={attendeeList}
                  keyExtractor={(item) => item.id.toString()}
                  renderItem={({ item }) => (
                    <AttendeeListItem
                      resident={item}
                      eventId={event.id}
                      onPress={() => {
                        setSelectedResident(item);
                        setResidentModalOpen(true);
                      }}
                    />
                  )}
                  style={styles.attendeeList}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.listContent}
                />
                {totalPages > 1 && (
                  <View
                    style={[
                      styles.paginationContainer,
                      { borderTopColor: redesign.card.border },
                    ]}
                  >
                    <View style={styles.paginationInfo}>
                      <Text
                        style={[
                          styles.paginationText,
                          { color: redesign.text.muted },
                        ]}
                      >
                        Page {currentPage + 1} of {totalPages}
                      </Text>
                    </View>
                    <View style={styles.paginationControls}>
                      <TouchableOpacity
                        style={[
                          styles.paginationButton,
                          { backgroundColor: redesign.accent.blueBg },
                          currentPage === 0 && {
                            backgroundColor: redesign.glass.buttonBg,
                          },
                        ]}
                        onPress={handlePreviousPage}
                        disabled={currentPage === 0 || isFetching}
                        accessibilityLabel="Previous page"
                        accessibilityHint={
                          currentPage === 0
                            ? 'No previous page available'
                            : 'Go to previous page of attendees'
                        }
                        accessibilityRole="button"
                      >
                        <Text
                          style={[
                            styles.paginationButtonText,
                            { color: semantic.text.link },
                          ]}
                        >
                          Previous
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.paginationButton,
                          { backgroundColor: redesign.accent.blueBg },
                          currentPage >= totalPages - 1 && {
                            backgroundColor: redesign.glass.buttonBg,
                          },
                          isFetching && pagingForward && styles.paginationButtonLoading,
                        ]}
                        onPress={handleNextPage}
                        disabled={currentPage >= totalPages - 1 || isFetching}
                        accessibilityLabel="Next page"
                        accessibilityHint={
                          currentPage >= totalPages - 1
                            ? 'No next page available'
                            : 'Go to next page of attendees'
                        }
                        accessibilityRole="button"
                      >
                        {isFetching && pagingForward ? (
                          <View style={styles.buttonSpinnerRow}>
                            <ActivityIndicator size="small" color={semantic.text.link} />
                          </View>
                        ) : (
                          <Text
                          style={[
                            styles.paginationButtonText,
                            { color: semantic.text.link },
                          ]}
                        >
                          Next
                        </Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            )}
          </View>

          {/* Modals (mobile) */}
          <FilterModal
            visible={showFilters}
            filters={attendeeFilters}
            onToggleFilter={toggleFilter}
            onClose={() => setShowFilters(false)}
          />

          <ResidentDetailsModal
            visible={residentModalOpen}
            resident={selectedResident}
            onClose={() => {
              setResidentModalOpen(false);
              setSelectedResident(null);
            }}
            onCheckIn={handleCheckIn}
            eventId={event.id}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    position: 'relative',
  },
  // Stats Section
  statsSection: {
    marginBottom: 24,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  // Controls Section
  controlsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  searchContainer: {
    flex: 1,
  },
  searchInputContainer: {
    marginBottom: 0,
  },
  searchWrapper: {
    position: 'relative',
    justifyContent: 'center',
  },
  searchSpinnerContainer: {
    position: 'absolute',
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    position: 'relative',
  },
  filterButtonIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 21,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  filterDropdownContainer: {
    position: 'relative',
    zIndex: 1001,
  },
  // List Section
  listSection: {
    flex: 1,
    position: 'relative',
  },
  listLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 48,
    backgroundColor: 'transparent',
    zIndex: 1000,
  },
  listLoadingInner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  listLoadingText: {
    marginTop: 6,
    fontSize: 12,
  },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  listTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  exportButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  exportButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  attendeeList: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 20,
  },
  // Empty State
  noAttendeesContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  noAttendeesText: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  noAttendeesSubtext: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  // Pagination
  paginationContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
  },
  paginationInfo: {
    alignItems: 'center',
    marginBottom: 12,
  },
  paginationText: {
    fontSize: 14,
    fontWeight: '500',
  },
  paginationControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  paginationButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  paginationButtonLoading: {
    opacity: 0.9,
  },
  buttonSpinnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationButtonText: {
    // color applied inline (semantic.text.link): the button fill is a ~10% blue
    // wash on which a white label is invisible in light mode.
    fontSize: 14,
    fontWeight: '600',
  },
  // Loading and Error States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 16,
    textAlign: 'center',
  },
  // Desktop split layout
  desktopRow: {
    flexDirection: 'row',
    gap: 16,
    minHeight: 0,
    flex: 1,
  },
  desktopListColumn: {
    flex: 1,
    minWidth: 360,
    maxWidth: 560,
    borderRightWidth: 1,
    paddingRight: 16,
    overflow: 'hidden',
  },
  desktopDetailsColumn: {
    flex: 1,
    paddingLeft: 16,
  },
  desktopEmptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 24,
    paddingBottom: 24,
  },
  desktopEmptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  desktopEmptySubtitle: {
    fontSize: 14,
  },
});
